import React, { useEffect, useRef, useState } from 'react';
import Map, { Marker, NavigationControl, GeolocateControl } from 'react-map-gl/maplibre';
import { MapPin, Navigation, ZoomIn, ZoomOut } from 'lucide-react';
import { Button } from '../button';
import { LocationCoordinates, LocationFeature } from '../../../types/location';
import { cn } from '../utils';
import 'maplibre-gl/dist/maplibre-gl.css';

// Map styles
const mapTilerKey = process.env.REACT_APP_MAPTILER_KEY;

const OSM_STYLE = {
    version: 8 as const,
    sources: {
        osm: {
            type: 'raster' as const,
            tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
            tileSize: 256,
            attribution: '© OpenStreetMap contributors'
        }
    },
    layers: [
        {
            id: 'osm',
            type: 'raster' as const,
            source: 'osm'
        }
    ]
};

const MAP_STYLE = mapTilerKey
    ? `https://api.maptiler.com/maps/streets/style.json?key=${mapTilerKey}`
    : OSM_STYLE;

interface MapDisplayProps {
    location?: LocationFeature | LocationCoordinates;
    className?: string;
    height?: number | string;
    zoom?: number;
    interactive?: boolean;
    showUserLocation?: boolean;
    showZoomControls?: boolean;
    showNavigationControl?: boolean;
    markers?: Array<{
        id: string;
        coordinates: LocationCoordinates;
        popup?: React.ReactNode;
        color?: string;
    }>;
    onLocationChange?: (coordinates: LocationCoordinates) => void;
    onZoomChange?: (zoom: number) => void;
}

export function MapDisplay({
    location,
    className,
    height = 400,
    zoom = 14,
    interactive = true,
    showUserLocation = false,
    showZoomControls = true,
    showNavigationControl = true,
    markers = [],
    onLocationChange,
    onZoomChange,
}: MapDisplayProps) {
    const mapRef = useRef<any>(null);
    const [viewState, setViewState] = useState({
        longitude: 0,
        latitude: 0,
        zoom: zoom,
    });
    const [userLocation, setUserLocation] = useState<LocationCoordinates | null>(null);

    // Update map center when location changes
    useEffect(() => {
        if (location) {
            let coordinates: LocationCoordinates;

            if ('center' in location) {
                coordinates = {
                    longitude: location.center[0],
                    latitude: location.center[1],
                };
            } else {
                coordinates = location as LocationCoordinates;
            }

            setViewState(prev => ({
                ...prev,
                longitude: coordinates.longitude,
                latitude: coordinates.latitude,
            }));
        }
    }, [location]);

    const handleMove = (evt: any) => {
        setViewState(evt.viewState);
        onLocationChange?.(evt.viewState);
        onZoomChange?.(evt.viewState.zoom);
    };

    const handleZoom = (direction: 'in' | 'out') => {
        const map = mapRef.current?.getMap();
        if (!map) return;

        if (direction === 'in') {
            map.zoomIn();
        } else {
            map.zoomOut();
        }
    };

    const handleLocateUser = () => {
        if (!navigator.geolocation) return;

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const coordinates = {
                    longitude: position.coords.longitude,
                    latitude: position.coords.latitude,
                };

                setUserLocation(coordinates);
                setViewState(prev => ({
                    ...prev,
                    ...coordinates,
                    zoom: 15,
                }));

                // Fly to user location
                const map = mapRef.current?.getMap();
                map?.flyTo({
                    center: [coordinates.longitude, coordinates.latitude],
                    zoom: 15,
                    essential: true
                });
            },
            (error) => {
                console.error('Geolocation error:', error);
            },
            { timeout: 10000, enableHighAccuracy: false }
        );
    };

    const getLocationCoordinates = (loc: LocationFeature | LocationCoordinates): LocationCoordinates => {
        if ('center' in loc) {
            return {
                longitude: loc.center[0],
                latitude: loc.center[1],
            };
        }
        return loc;
    };

    return (
        <div className={cn('relative overflow-hidden rounded-lg', className)}>
            <Map
                ref={mapRef}
                {...viewState}
                onMove={handleMove}
                style={{ width: '100%', height: height }}
                mapStyle={MAP_STYLE}
                attributionControl={false}
                interactive={interactive}
            >
                {/* Navigation and Geolocation Controls */}
                {showNavigationControl && (
                    <NavigationControl position="top-right" />
                )}

                {showUserLocation && (
                    <GeolocateControl
                        position="top-right"
                        onTrackUserLocationEnd={() => { }}
                        onError={() => { }}
                        onOutOfMaxBounds={() => { }}
                        onGeolocate={() => { }}
                    />
                )}

                {/* Main Location Marker */}
                {location && (
                    <Marker
                        {...getLocationCoordinates(location)}
                        anchor="bottom"
                    >
                        <div className="relative">
                            <div className="w-6 h-6 bg-primary border-2 border-white rounded-full shadow-lg flex items-center justify-center">
                                <MapPin className="w-3 h-3 text-white" />
                            </div>
                            <div className="absolute -inset-2 bg-primary/20 rounded-full animate-ping" />
                        </div>
                    </Marker>
                )}

                {/* User Location Marker */}
                {userLocation && (
                    <Marker
                        longitude={userLocation.longitude}
                        latitude={userLocation.latitude}
                        anchor="center"
                    >
                        <div className="relative">
                            <div className="w-3 h-3 bg-blue-500 border-2 border-white rounded-full shadow" />
                            <div className="absolute -inset-2 bg-blue-400 rounded-full opacity-30 animate-ping" />
                        </div>
                    </Marker>
                )}

                {/* Additional Markers */}
                {markers.map((marker) => (
                    <Marker
                        key={marker.id}
                        longitude={marker.coordinates.longitude}
                        latitude={marker.coordinates.latitude}
                        anchor="bottom"
                    >
                        <div className="relative">
                            <div
                                className={cn(
                                    'w-6 h-6 border-2 border-white rounded-full shadow-lg flex items-center justify-center',
                                    marker.color || 'bg-secondary'
                                )}
                            >
                                <MapPin className="w-3 h-3 text-white" />
                            </div>
                        </div>
                    </Marker>
                ))}
            </Map>

            {/* Custom Zoom Controls */}
            {showZoomControls && interactive && (
                <div className="absolute top-4 right-4 flex flex-col space-y-2 z-10">
                    <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleZoom('in')}
                        className="w-8 h-8 p-0 shadow-md"
                    >
                        <ZoomIn size={16} />
                    </Button>
                    <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleZoom('out')}
                        className="w-8 h-8 p-0 shadow-md"
                    >
                        <ZoomOut size={16} />
                    </Button>
                    {showUserLocation && (
                        <Button
                            size="sm"
                            variant="secondary"
                            onClick={handleLocateUser}
                            className="w-8 h-8 p-0 shadow-md"
                        >
                            <Navigation size={16} />
                        </Button>
                    )}
                </div>
            )}

            {/* Zoom Level Indicator */}
            <div className="absolute bottom-4 left-4 bg-white/90 dark:bg-gray-800/90 rounded px-3 py-1 text-sm shadow-md">
                Zoom: {Math.round(viewState.zoom * 10) / 10}
            </div>

            {/* Attribution */}
            <div className="absolute bottom-1 right-1 text-xs text-gray-500">
                {mapTilerKey ? '© MapTiler' : '© OpenStreetMap'}
            </div>
        </div>
    );
}
